import { Avatar, AvatarProps } from '@components/shared/avatar.component';
import { cn } from '@lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import React from 'react';

const avatarItemVariants = cva('flex items-start min-w-0', {
  variants: {
    variant: {
      xs: 'gap-2',
      sm: 'gap-2.5',
      md: 'gap-3',
      lg: 'gap-4',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

const titleVariants = cva('font-normal truncate', {
  variants: {
    variant: {
      xs: 'text-xs leading-3',
      sm: 'text-sm leading-4',
      md: 'text-base leading-4',
      lg: 'text-lg leading-5',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

const subtitleVariants = cva('truncate flex items-center min-w-0', {
  variants: {
    variant: {
      xs: 'text-[10px] gap-1',
      sm: 'text-xs gap-1.5',
      md: 'text-xs gap-1.5',
      lg: 'text-sm gap-2',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

const descriptionVariants = cva('truncate', {
  variants: {
    variant: {
      xs: 'text-[10px]',
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

const textContainerVariants = cva('flex flex-col min-w-0 overflow-hidden', {
  variants: {
    variant: {
      xs: 'gap-0.5',
      sm: 'gap-1',
      md: 'gap-1',
      lg: 'gap-1.5',
    },
  },
  defaultVariants: {
    variant: 'md',
  },
});

export type AvatarItemProps = AvatarProps & {
  badge?: React.ReactNode;
  className?: string;
  colorMode?: 'light' | 'dark';
  description?: React.ReactNode;
  imageUrl?: string;
  subtitle?: React.ReactNode;
  title: React.ReactNode;
  variant?: VariantProps<typeof avatarItemVariants>['variant'];
};

export const AvatarItem: React.FC<AvatarItemProps> = (props) => {
  const {
    altText,
    badge,
    className,
    colorMode = 'light',
    description,
    imageUrl,
    initialText,
    size,
    subtitle,
    title,
    variant,
  } = props;

  const titleColor = colorMode === 'light' ? 'text-gray-900' : 'text-white';
  const subtitleColor = colorMode === 'light' ? 'text-[#1F1C1AB2]' : 'text-white/70';

  return (
    <div className={cn(avatarItemVariants({ variant }), className)}>
      <Avatar
        altText={altText}
        className="shrink-0"
        colorMode={colorMode}
        imageUrl={imageUrl}
        initialText={initialText ?? (typeof title === 'string' ? title : '')}
        size={size}
      />
      <div className={textContainerVariants({ variant })}>
        <div className={cn(titleVariants({ variant }), titleColor)}>{title}</div>
        {subtitle && (
          <div className={cn(subtitleVariants({ variant }), subtitleColor)}>
            {badge}
            <span className="truncate">{subtitle}</span>
          </div>
        )}
        {description && <div className={cn(descriptionVariants({ variant }), subtitleColor)}>{description}</div>}
      </div>
    </div>
  );
};
